import flowRight from 'lodash/flowRight';
import React from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';

import { moment } from '@eventbrite/date';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { DatePicker } from '@eventbrite/eds-date-picker';
import { ORANGE_COLOR } from '@eventbrite/eds-date-picker';
import {
    STYLE_DYNAMIC,
    MIN_ASIDE_SPACING_SUFFIX,
} from '@eventbrite/eds-input-field';
import { CrossChunky } from '@eventbrite/eds-iconography';
import { IconButton } from '@eventbrite/eds-icon-button';
import { formValidate } from '@eventbrite/eds-utils';

import { getSeriesCalendar } from '../../selectors/series';
import { gettext } from '@eventbrite/i18n';
import {
    clearSeriesEvents as clearSeriesEventsAction,
    getSeriesEvents as getSeriesEventsAction,
    getSeriesEventsByDate as getSeriesEventsByDateAction,
} from '../../actions/series';
import { trackDatePickerSelectionClear } from '../../actions/eventAnalytics';
import { EVENT_CANCELED_STATUS } from '../status/constants';
import { EVENT_SERIES_FORM_NAME } from '../../constants';

// DatePicker will disable the minDate as well
// so to keep the current date enabled the minDate has to be a day before today.
const YESTERDAY = moment().subtract(1, 'day').format('YYYY-MM-DD');

/**
 * Generates an isDayHighlighted function that returns true
 * if the day has any events for the current calendar.
 *
 * @param {Map<String, Array<Event>>} calendar a map of avialbale dates for the current event
 * and a list of all the events happening on that date.
 */
const hasEventsOnDay = (calendar) => (day) =>
    Boolean(calendar[day.format('YYYY-MM-DD')]);

/**
 * Generates an isDayBlocked function that returns true
 * if the day has NO events or all the events are sold out for the current calendar.
 *
 * @param {Map<String, Array<Event>>} calendar a map of avialbale dates for the current event
 * and a list of all the events happening on that date.
 */
const hasNoEventsAvailableOnDay = (calendar) => (day) => {
    const eventsOnDay = calendar[day.format('YYYY-MM-DD')];

    return (
        !eventsOnDay ||
        eventsOnDay.every(
            (event) =>
                (event.soldOut && !event.hasWaitlistAvailable) ||
                event.status === EVENT_CANCELED_STATUS,
        )
    );
};

const SeriesEventDatePicker = ({
    calendar = {},
    onFilterDateChange,
    onFilterDateFocus,
    onClearDateClick,
    onNextMonthClick,
    onPrevMonthClick,
}) =>
    Object.keys(calendar).length ? (
        <div
            className="eds-g-cell-sm-12-12 eds-g-cell-md-6-12"
            data-automation="series-events-date-picker"
        >
            <ValidationFormField name="filterDate" v2={true}>
                <DatePicker
                    label={gettext('Filter by Date')}
                    style={STYLE_DYNAMIC}
                    color={ORANGE_COLOR}
                    // Force calendar on mobile instad of native datepicker
                    shouldUsePicker={true}
                    // Prevent users from typing random dates, can only select available dates
                    readOnly={true}
                    minDate={YESTERDAY}
                    isDayHighlighted={hasEventsOnDay(calendar)}
                    isDayBlocked={hasNoEventsAvailableOnDay(calendar)}
                    onChange={onFilterDateChange}
                    onFocus={onFilterDateFocus}
                    minAsideSpacing={MIN_ASIDE_SPACING_SUFFIX}
                    suffix={
                        <IconButton
                            iconType={<CrossChunky />}
                            title={gettext('Clear date')}
                            onClick={onClearDateClick}
                        />
                    }
                    onPrevMonthClick={onPrevMonthClick}
                    onNextMonthClick={onNextMonthClick}
                />
            </ValidationFormField>
        </div>
    ) : null;

const getMonthRange = (date) => {
    const startDate = moment(date).startOf('month');
    const endDate = moment(date).endOf('month');

    return { startDate, endDate };
};

const SeriesEventDatePickerContainer = flowRight(
    connect(
        (state) => ({
            calendar: getSeriesCalendar(state),
        }),
        (dispatch) => ({
            onClearDateClick: () => {
                dispatch(trackDatePickerSelectionClear());
                dispatch(change(EVENT_SERIES_FORM_NAME, 'filterDate', ''));
            },
            onFilterDateChange: (startDate) => {
                if (startDate) {
                    dispatch(getSeriesEventsByDateAction({ startDate }));
                }
            },
            onFilterDateFocus: () => {
                const today = moment();
                const { startDate, endDate } = getMonthRange(today);

                dispatch(
                    getSeriesEventsAction({
                        startDate,
                        endDate,
                        showLoader: false,
                    }),
                );
            },
            onPrevMonthClick: (date) => {
                const { startDate, endDate } = getMonthRange(date);

                dispatch(
                    getSeriesEventsAction({
                        startDate,
                        endDate,
                        showLoader: false,
                    }),
                );
            },
            onNextMonthClick: (date) => {
                const { startDate, endDate } = getMonthRange(date);

                dispatch(
                    getSeriesEventsAction({
                        startDate,
                        endDate,
                        showLoader: false,
                    }),
                );
            },
        }),
    ),
    formValidate({ form: EVENT_SERIES_FORM_NAME }),
)(SeriesEventDatePicker);

export default SeriesEventDatePickerContainer;
